import React, { useEffect, useState, useCallback, useRef } from 'react';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import PageHeader from '../../../components/common/PageHeader';
import AlertToast from '../../../components/common/AlertToast';
import Pagination from '../../../components/common/Pagination';

const DEFAULT_CLASSES = [
  'Pre-Primary (Nursery)', 'Pre-Primary (LKG)', 'Pre-Primary (UKG)',
  'Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5',
  'Class 6', 'Class 7', 'Class 8', 'Class 9', 'Class 10',
  'Class 11 (Science)', 'Class 11 (Commerce)', 'Class 11 (Arts)',
  'Class 12 (Science)', 'Class 12 (Commerce)', 'Class 12 (Arts)',
];
const DEFAULT_SUBJECTS = [
  'Mathematics', 'Science', 'English', 'Social Studies', 'Hindi', 'Tamil',
  'Computer Science', 'Physics', 'Chemistry', 'Biology', 'History',
  'Geography', 'Economics', 'Commerce', 'Accountancy', 'Business Studies',
  'Environmental Science', 'General Knowledge',
];
const BOARDS = [
  'CBSE', 'ICSE', 'State Board (Tamil Nadu)', 'State Board (Karnataka)',
  'State Board (Maharashtra)', 'State Board (Kerala)', 'State Board (Andhra Pradesh)',
  'State Board (Telangana)', 'IB (International Baccalaureate)', 'IGCSE', 'Other',
];
const NOTE_TYPES = ['Chapter Notes', 'Summary', 'Mind Map', 'Practice Questions', 'Revision Sheet', 'Formula Sheet', 'Glossary', 'Other'];

const blank = {
  title: '', class_grade: '', subject: '', chapter: '', note_type: 'Chapter Notes',
  board: '', content: '', tags: '', session: '',
};

export default function CourseNotesPage({ moduleKey = 'course_notes' }) {
  const { user }  = useAuth();
  const isAdmin   = ['admin', 'manager', 'teacher', 'faculty', 'principal'].includes(user?.roleKey);

  const [notes,        setNotes]        = useState([]);
  const [total,        setTotal]        = useState(0);
  const [page,         setPage]         = useState(1);
  const [loading,      setLoading]      = useState(true);
  const [activeTab,    setActiveTab]    = useState('browse');
  const [showForm,     setShowForm]     = useState(false);
  const [editing,      setEditing]      = useState(null);
  const [form,         setForm]         = useState(blank);
  const [saving,       setSaving]       = useState(false);
  const [toast,        setToast]        = useState({ show: false, message: '', type: 'success' });
  const [classOptions, setClassOptions] = useState(DEFAULT_CLASSES);
  const [subjectOpts,  setSubjectOpts]  = useState(DEFAULT_SUBJECTS);
  const [filterClass,  setFilterClass]  = useState('');
  const [filterSub,    setFilterSub]    = useState('');
  const [filterType,   setFilterType]   = useState('');
  const [searchQuery,  setSearchQuery]  = useState('');
  const [viewNote,     setViewNote]     = useState(null);

  // AI generation state
  const [aiPrompt,     setAiPrompt]     = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiResult,     setAiResult]     = useState('');

  // PDF import state
  const [pdfFile,      setPdfFile]      = useState(null);
  const [pdfParsing,   setPdfParsing]   = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    api.get('/records/school_config', { params: { limit: 1 } })
      .then(r => {
        const rec = r.data?.records?.[0]?.recordData || r.data?.record?.recordData;
        if (rec) {
          if (rec.classes?.length)  setClassOptions(rec.classes);
          if (rec.subjects?.length) setSubjectOpts(rec.subjects);
        }
      }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const params = { page, limit: 20 };
    if (filterClass) params['recordData.class_grade'] = filterClass;
    if (filterSub)   params['recordData.subject']     = filterSub;
    if (filterType)  params['recordData.note_type']   = filterType;
    if (searchQuery) params.search = searchQuery;
    const r = await api.get(`/records/${moduleKey}`, { params })
      .catch(() => ({ data: { records: [], total: 0 } }));
    setNotes(r.data.records || []);
    setTotal(r.data.total || 0);
    setLoading(false);
  }, [moduleKey, page, filterClass, filterSub, filterType, searchQuery]);

  useEffect(() => { load(); }, [load]);

  const sf = k => v => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editing?._id) await api.put(`/records/${moduleKey}/${editing._id}`, form);
      else              await api.post(`/records/${moduleKey}`, form);
      setToast({ show: true, message: `Notes ${editing ? 'updated' : 'saved'}!`, type: 'success' });
      setShowForm(false); setEditing(null); setForm(blank); load();
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || 'Error saving', type: 'danger' });
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete these notes?')) return;
    await api.delete(`/records/${moduleKey}/${id}`).catch(() => {});
    load();
  };

  const openEdit = (note) => {
    setForm({ ...blank, ...note.recordData });
    setEditing(note); setShowForm(true); setActiveTab('create'); setViewNote(null);
  };

  const openNew = () => {
    setForm(blank); setEditing(null); setShowForm(true); setActiveTab('create');
  };

  // AI Generation
  const generateWithAI = async () => {
    if (!aiPrompt.trim()) return;
    setAiGenerating(true);
    try {
      const r = await api.post('/education/notes/generate', {
        prompt: aiPrompt,
        class_grade: form.class_grade,
        subject: form.subject,
        board: form.board,
        chapter: form.chapter,
      });
      const generated = r.data?.content || r.data?.text || '';
      setAiResult(generated);
      setForm(f => ({ ...f, content: generated }));
      setToast({ show: true, message: 'Notes generated! Review and edit below.', type: 'success' });
    } catch {
      // Fallback: generate template
      const template = buildTemplate(form);
      setAiResult(template);
      setForm(f => ({ ...f, content: template }));
      setToast({ show: true, message: 'Generated a template — please fill in the details.', type: 'info' });
    } finally { setAiGenerating(false); }
  };

  function buildTemplate(f) {
    const cls = f.class_grade || '[Class]';
    const sub = f.subject     || '[Subject]';
    const ch  = f.chapter     || '[Chapter]';
    const brd = f.board       || '[Board]';
    return `# ${ch} — ${sub} (${cls})
Board: ${brd}

## Learning Objectives
- [ ] Objective 1
- [ ] Objective 2
- [ ] Objective 3

## Key Concepts
### Concept 1
[Explain concept 1 here]

### Concept 2
[Explain concept 2 here]

## Important Points
1. Point 1
2. Point 2
3. Point 3

## Definitions
- **Term 1**: Definition
- **Term 2**: Definition

## Formulas / Equations
(Add relevant formulas here)

## Summary
(Write a brief summary of the chapter)

## Practice Questions
1. Question 1
2. Question 2
3. Question 3
`;
  }

  // PDF import (reads as text — basic extraction)
  const handlePdfImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPdfFile(file);
    setPdfParsing(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const r = await api.post('/education/notes/parse-pdf', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const text = r.data?.text || r.data?.content || '';
      setForm(f => ({ ...f, content: text, title: f.title || file.name.replace('.pdf', '') }));
      setToast({ show: true, message: 'PDF content extracted! Review and edit.', type: 'success' });
    } catch {
      // Fallback: just set the filename
      setForm(f => ({ ...f, title: f.title || file.name.replace('.pdf', ''), content: f.content + `\n\n[PDF imported: ${file.name}]\n[Content extraction not available — please paste or type the notes below]` }));
      setToast({ show: true, message: 'PDF uploaded. Please type or paste the notes content.', type: 'warning' });
    } finally { setPdfParsing(false); }
  };

  // AI prompt builder
  const buildAiPrompt = () => {
    const parts = [];
    if (form.board)        parts.push(`${form.board} board`);
    if (form.class_grade)  parts.push(form.class_grade);
    if (form.subject)      parts.push(form.subject);
    if (form.chapter)      parts.push(`chapter: ${form.chapter}`);
    const base = parts.join(', ');
    setAiPrompt(`Generate detailed ${form.note_type || 'chapter notes'} for ${base || 'the selected class and subject'}. Include key concepts, definitions, examples, and practice questions.`);
  };

  const NOTE_TYPE_COLORS = {
    'Chapter Notes': '#6366f1', 'Summary': '#0ea5e9', 'Mind Map': '#8b5cf6',
    'Practice Questions': '#ef4444', 'Revision Sheet': '#f59e0b',
    'Formula Sheet': '#22c55e', 'Glossary': '#64748b', 'Other': '#94a3b8',
  };

  return (
    <div>
      <PageHeader
        title="Course Notes"
        icon="bi-journal-text"
        subtitle="Study materials, notes and resources for all classes"
        actions={
          isAdmin && (
            <button className="btn btn-primary btn-sm" onClick={openNew}>
              <i className="bi bi-plus-lg me-1"></i>Add Notes
            </button>
          )
        }
      />

      {/* Main tabs */}
      <ul className="nav nav-tabs mb-3">
        {[
          { key: 'browse', label: 'Browse Notes', icon: 'bi-search' },
          { key: 'create', label: 'Create / Edit',icon: 'bi-plus-circle', adminOnly: true },
          { key: 'ai',     label: 'AI Generator', icon: 'bi-robot',       adminOnly: true },
          { key: 'import', label: 'Import PDF',   icon: 'bi-file-earmark-pdf', adminOnly: true },
        ].filter(t => !t.adminOnly || isAdmin).map(t => (
          <li className="nav-item" key={t.key}>
            <button
              className={`nav-link ${activeTab === t.key ? 'active fw-semibold' : ''}`}
              onClick={() => setActiveTab(t.key)}>
              <i className={`bi ${t.icon} me-1`}></i>{t.label}
            </button>
          </li>
        ))}
      </ul>

      {/* ── BROWSE TAB ─────────────────────────────────────────────── */}
      {activeTab === 'browse' && (
        <>
          {/* Filters */}
          <div className="card mb-3">
            <div className="card-body py-2">
              <div className="row g-2 align-items-center">
                <div className="col-md-3">
                  <input type="text" className="form-control form-control-sm"
                    placeholder="Search notes…" value={searchQuery}
                    onChange={e => { setSearchQuery(e.target.value); setPage(1); }} />
                </div>
                <div className="col-md-3">
                  <select className="form-select form-select-sm" value={filterClass}
                    onChange={e => { setFilterClass(e.target.value); setPage(1); }}>
                    <option value="">All Classes</option>
                    {classOptions.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div className="col-md-3">
                  <select className="form-select form-select-sm" value={filterSub}
                    onChange={e => { setFilterSub(e.target.value); setPage(1); }}>
                    <option value="">All Subjects</option>
                    {subjectOpts.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div className="col-md-3">
                  <select className="form-select form-select-sm" value={filterType}
                    onChange={e => { setFilterType(e.target.value); setPage(1); }}>
                    <option value="">All Types</option>
                    {NOTE_TYPES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* View single note */}
          {viewNote && (
            <div className="card mb-3" style={{ border: '2px solid #e0e7ff', borderRadius: 12 }}>
              <div className="card-header d-flex justify-content-between align-items-center"
                style={{ background: '#f5f3ff' }}>
                <span style={{ fontWeight: 700, fontSize: 14 }}>
                  <i className="bi bi-journal-text me-2 text-primary"></i>
                  {viewNote.recordData?.title}
                </span>
                <div className="d-flex gap-2">
                  {isAdmin && (
                    <button className="btn btn-outline-primary btn-sm" onClick={() => openEdit(viewNote)}>
                      <i className="bi bi-pencil me-1"></i>Edit
                    </button>
                  )}
                  <button className="btn-close" onClick={() => setViewNote(null)}></button>
                </div>
              </div>
              <div className="card-body">
                <div className="d-flex flex-wrap gap-2 mb-3">
                  {[
                    { label: viewNote.recordData?.class_grade, icon: 'bi-mortarboard' },
                    { label: viewNote.recordData?.subject,     icon: 'bi-book' },
                    { label: viewNote.recordData?.chapter,     icon: 'bi-bookmark' },
                    { label: viewNote.recordData?.board,       icon: 'bi-award' },
                    { label: viewNote.recordData?.session,     icon: 'bi-calendar3' },
                  ].filter(b => b.label).map((b, i) => (
                    <span key={i} className="badge"
                      style={{ background: '#e0e7ff', color: '#4f46e5', fontSize: 12, padding: '5px 10px' }}>
                      <i className={`bi ${b.icon} me-1`}></i>{b.label}
                    </span>
                  ))}
                  <span className="badge"
                    style={{
                      background: `${NOTE_TYPE_COLORS[viewNote.recordData?.note_type] || '#6366f1'}20`,
                      color: NOTE_TYPE_COLORS[viewNote.recordData?.note_type] || '#6366f1',
                      fontSize: 12, padding: '5px 10px',
                    }}>
                    {viewNote.recordData?.note_type}
                  </span>
                </div>
                <div style={{
                  fontSize: 14, lineHeight: 1.9, whiteSpace: 'pre-wrap', color: '#334155',
                  background: '#f8fafc', padding: 16, borderRadius: 8, border: '1px solid #e2e8f0',
                  maxHeight: 500, overflowY: 'auto',
                }}>
                  {viewNote.recordData?.content || <span className="text-muted">No content</span>}
                </div>
                {viewNote.recordData?.tags && (
                  <div className="mt-3 d-flex flex-wrap gap-1">
                    {viewNote.recordData.tags.split(',').map(tag => (
                      <span key={tag.trim()} className="badge bg-secondary bg-opacity-25 text-secondary"
                        style={{ fontSize: 11 }}>#{tag.trim()}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notes grid */}
          {loading ? (
            <div className="text-center py-5"><span className="spinner-border text-primary"></span></div>
          ) : notes.length === 0 ? (
            <div className="card text-center py-5 text-muted">
              <i className="bi bi-journal-text display-4 d-block mb-2 opacity-25"></i>
              No notes found.
              {isAdmin && (
                <> <button className="btn btn-link p-0" onClick={openNew}>Add the first notes</button></>
              )}
            </div>
          ) : (
            <div className="row g-3">
              {notes.map(note => {
                const d = note.recordData || {};
                const typeColor = NOTE_TYPE_COLORS[d.note_type] || '#6366f1';
                return (
                  <div className="col-md-4 col-lg-3" key={note._id}>
                    <div className="card h-100 shadow-sm"
                      style={{ borderRadius: 12, borderTop: `3px solid ${typeColor}`, cursor: 'pointer' }}
                      onClick={() => setViewNote(note)}>
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <span className="badge"
                            style={{ background: typeColor + '20', color: typeColor, fontSize: 10 }}>
                            {d.note_type || 'Notes'}
                          </span>
                          {isAdmin && (
                            <div className="d-flex gap-1" onClick={e => e.stopPropagation()}>
                              <button className="btn btn-outline-primary btn-sm"
                                style={{ padding: '1px 5px', fontSize: 10 }}
                                onClick={() => openEdit(note)}>
                                <i className="bi bi-pencil"></i>
                              </button>
                              <button className="btn btn-outline-danger btn-sm"
                                style={{ padding: '1px 5px', fontSize: 10 }}
                                onClick={() => handleDelete(note._id)}>
                                <i className="bi bi-trash"></i>
                              </button>
                            </div>
                          )}
                        </div>
                        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6, lineHeight: 1.4 }}>
                          {d.title || 'Untitled'}
                        </div>
                        <div className="d-flex flex-wrap gap-1 mb-2">
                          {d.class_grade && (
                            <span style={{ fontSize: 11, color: '#64748b' }}>
                              <i className="bi bi-mortarboard me-1"></i>{d.class_grade}
                            </span>
                          )}
                          {d.subject && (
                            <span style={{ fontSize: 11, color: '#64748b' }}>
                              · <i className="bi bi-book me-1"></i>{d.subject}
                            </span>
                          )}
                        </div>
                        {d.chapter && (
                          <div style={{ fontSize: 12, color: '#94a3b8' }}>
                            <i className="bi bi-bookmark me-1"></i>{d.chapter}
                          </div>
                        )}
                        {d.board && (
                          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                            <i className="bi bi-award me-1"></i>{d.board}
                          </div>
                        )}
                        {d.content && (
                          <div style={{
                            fontSize: 12, color: '#94a3b8', marginTop: 8,
                            overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                          }}>
                            {d.content.substring(0, 120)}…
                          </div>
                        )}
                      </div>
                      <div className="card-footer py-1" style={{ fontSize: 11, color: '#94a3b8', background: '#f8fafc' }}>
                        {new Date(note.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <Pagination page={page} totalPages={Math.ceil(total / 20)} onPageChange={setPage} total={total} limit={20} />
        </>
      )}

      {/* ── CREATE / EDIT TAB ─────────────────────────────────────── */}
      {activeTab === 'create' && isAdmin && (
        <div className="card" style={{ borderRadius: 12 }}>
          <div className="card-header fw-semibold" style={{ fontSize: 14 }}>
            <i className="bi bi-pencil me-2 text-primary"></i>
            {editing ? `Edit: ${editing.recordData?.title}` : 'Create New Notes'}
          </div>
          <div className="card-body">
            <form onSubmit={handleSave}>
              <div className="row g-3">
                <div className="col-md-5">
                  <label className="form-label fw-semibold">Title <span className="text-danger">*</span></label>
                  <input className="form-control" required value={form.title}
                    onChange={e => sf('title')(e.target.value)}
                    placeholder="e.g. Chapter 5 — Photosynthesis Notes" />
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-semibold">Note Type</label>
                  <select className="form-select" value={form.note_type}
                    onChange={e => sf('note_type')(e.target.value)}>
                    {NOTE_TYPES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold">Board / Curriculum</label>
                  <select className="form-select" value={form.board}
                    onChange={e => sf('board')(e.target.value)}>
                    <option value="">Select board</option>
                    {BOARDS.map(b => <option key={b}>{b}</option>)}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold">Class / Grade <span className="text-danger">*</span></label>
                  <select className="form-select" required value={form.class_grade}
                    onChange={e => sf('class_grade')(e.target.value)}>
                    <option value="">Select class</option>
                    {classOptions.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold">Subject <span className="text-danger">*</span></label>
                  <select className="form-select" required value={form.subject}
                    onChange={e => sf('subject')(e.target.value)}>
                    <option value="">Select subject</option>
                    {subjectOpts.map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-semibold">Session / Academic Year</label>
                  <input className="form-control" placeholder="e.g. 2024-25"
                    value={form.session || ''} onChange={e => sf('session')(e.target.value)} />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Chapter / Topic</label>
                  <input className="form-control" placeholder="e.g. Chapter 5 — Chemical Bonding"
                    value={form.chapter || ''} onChange={e => sf('chapter')(e.target.value)} />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Tags <span style={{ fontWeight: 400, color: '#94a3b8', fontSize: 11 }}>(comma separated)</span></label>
                  <input className="form-control" placeholder="e.g. photosynthesis, biology, class10"
                    value={form.tags || ''} onChange={e => sf('tags')(e.target.value)} />
                </div>

                {/* AI helper */}
                <div className="col-12">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <label className="form-label fw-semibold mb-0">Notes Content <span className="text-danger">*</span></label>
                    <button type="button" className="btn btn-sm"
                      style={{ background: '#ede9fe', color: '#6366f1', fontSize: 11, padding: '2px 10px' }}
                      onClick={() => { buildAiPrompt(); setActiveTab('ai'); }}>
                      <i className="bi bi-robot me-1"></i>Generate with AI
                    </button>
                    <button type="button" className="btn btn-sm"
                      style={{ background: '#e0f2fe', color: '#0369a1', fontSize: 11, padding: '2px 10px' }}
                      onClick={() => fileInputRef.current?.click()}>
                      <i className="bi bi-file-earmark-pdf me-1"></i>Import PDF
                    </button>
                    <input type="file" ref={fileInputRef} accept=".pdf,.doc,.docx,.txt"
                      style={{ display: 'none' }} onChange={handlePdfImport} />
                    {pdfParsing && (
                      <span className="spinner-border spinner-border-sm text-primary"></span>
                    )}
                  </div>
                  <textarea className="form-control" rows={16} required
                    value={form.content} onChange={e => sf('content')(e.target.value)}
                    placeholder="Type or paste notes content here…
You can use markdown-style formatting:
# Heading
## Sub-heading
- Bullet point
**bold text**
" />
                  <div className="form-text">
                    Supports plain text and basic markdown formatting. Use the AI generator or PDF import for quick content.
                  </div>
                </div>
              </div>
              <div className="d-flex gap-2 justify-content-end mt-3 pt-2 border-top">
                {editing && (
                  <button type="button" className="btn btn-secondary btn-sm"
                    onClick={() => { setEditing(null); setForm(blank); }}>
                    Clear / New
                  </button>
                )}
                <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
                  {saving && <span className="spinner-border spinner-border-sm me-1"></span>}
                  {editing ? 'Update Notes' : 'Save Notes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── AI GENERATOR TAB ──────────────────────────────────────── */}
      {activeTab === 'ai' && isAdmin && (
        <div className="row g-3">
          <div className="col-lg-5">
            <div className="card h-100">
              <div className="card-header fw-semibold" style={{ fontSize: 14 }}>
                <i className="bi bi-robot me-2 text-primary"></i>AI Notes Generator
              </div>
              <div className="card-body">
                <div className="alert alert-info" style={{ fontSize: 12 }}>
                  <i className="bi bi-lightbulb me-1"></i>
                  Fill in the subject details below, then write what kind of notes you want. The AI will generate structured notes based on the board's curriculum.
                </div>

                <div className="row g-3 mb-3">
                  <div className="col-6">
                    <label className="form-label fw-semibold">Board</label>
                    <select className="form-select form-select-sm" value={form.board}
                      onChange={e => sf('board')(e.target.value)}>
                      <option value="">Select board</option>
                      {BOARDS.map(b => <option key={b}>{b}</option>)}
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-semibold">Class</label>
                    <select className="form-select form-select-sm" value={form.class_grade}
                      onChange={e => sf('class_grade')(e.target.value)}>
                      <option value="">Select</option>
                      {classOptions.map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-semibold">Subject</label>
                    <select className="form-select form-select-sm" value={form.subject}
                      onChange={e => sf('subject')(e.target.value)}>
                      <option value="">Select</option>
                      {subjectOpts.map(s => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="form-label fw-semibold">Chapter / Topic</label>
                    <input className="form-control form-control-sm"
                      placeholder="e.g. Photosynthesis"
                      value={form.chapter || ''} onChange={e => sf('chapter')(e.target.value)} />
                  </div>
                  <div className="col-12">
                    <label className="form-label fw-semibold">Note Type</label>
                    <select className="form-select form-select-sm" value={form.note_type}
                      onChange={e => sf('note_type')(e.target.value)}>
                      {NOTE_TYPES.map(t => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                </div>

                <label className="form-label fw-semibold">Prompt / Instructions</label>
                <textarea className="form-control mb-2" rows={5}
                  value={aiPrompt}
                  onChange={e => setAiPrompt(e.target.value)}
                  placeholder="e.g. Generate detailed CBSE Class 10 Science notes on Photosynthesis including key terms, process steps, diagrams description, and 5 practice questions…" />

                <div className="d-flex gap-2">
                  <button className="btn btn-outline-secondary btn-sm" onClick={buildAiPrompt}>
                    <i className="bi bi-magic me-1"></i>Auto-fill prompt
                  </button>
                  <button className="btn btn-primary btn-sm flex-grow-1"
                    onClick={generateWithAI}
                    disabled={aiGenerating || !aiPrompt.trim()}>
                    {aiGenerating
                      ? <><span className="spinner-border spinner-border-sm me-1"></span>Generating…</>
                      : <><i className="bi bi-robot me-1"></i>Generate Notes</>}
                  </button>
                </div>

                {aiResult && (
                  <div className="mt-3">
                    <div className="d-flex gap-2">
                      <button className="btn btn-success btn-sm flex-grow-1"
                        onClick={() => { setForm(f => ({ ...f, content: aiResult })); setActiveTab('create'); }}>
                        <i className="bi bi-check2 me-1"></i>Use This Content
                      </button>
                      <button className="btn btn-outline-secondary btn-sm"
                        onClick={() => setAiResult('')}>Clear</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-lg-7">
            <div className="card h-100">
              <div className="card-header fw-semibold" style={{ fontSize: 14 }}>
                <i className="bi bi-eye me-2 text-primary"></i>Generated Preview
              </div>
              <div className="card-body p-0">
                {aiResult ? (
                  <textarea className="form-control border-0 rounded-0"
                    rows={28}
                    value={aiResult}
                    onChange={e => setAiResult(e.target.value)}
                    style={{ resize: 'none', fontFamily: 'monospace', fontSize: 13 }} />
                ) : (
                  <div className="text-center text-muted py-5">
                    <i className="bi bi-robot display-4 d-block mb-3 opacity-25"></i>
                    Fill in the details and click "Generate Notes" to see the result here
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── PDF IMPORT TAB ─────────────────────────────────────────── */}
      {activeTab === 'import' && isAdmin && (
        <div className="row g-3">
          <div className="col-lg-6">
            <div className="card">
              <div className="card-header fw-semibold" style={{ fontSize: 14 }}>
                <i className="bi bi-file-earmark-pdf me-2 text-primary"></i>Import from Document
              </div>
              <div className="card-body">
                <p style={{ fontSize: 13, color: '#64748b' }}>
                  Upload a PDF, Word document, or text file to import notes. The system will extract text content which you can then review and save.
                </p>

                {/* Drag & drop zone */}
                <div
                  className="text-center p-5 rounded mb-3"
                  style={{
                    border: '2px dashed #c7d2fe', background: '#f5f3ff', cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={e => e.preventDefault()}
                  onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) { const fakeE = { target: { files: [f] } }; handlePdfImport(fakeE); } }}>
                  {pdfParsing ? (
                    <><span className="spinner-border text-primary mb-2"></span><div style={{ fontSize: 13 }}>Parsing document…</div></>
                  ) : (
                    <>
                      <i className="bi bi-cloud-upload text-primary" style={{ fontSize: 40 }}></i>
                      <div style={{ fontWeight: 600, marginTop: 8 }}>Click to upload or drag &amp; drop</div>
                      <div style={{ fontSize: 12, color: '#94a3b8' }}>PDF, DOC, DOCX, TXT supported</div>
                      {pdfFile && (
                        <div className="badge bg-success mt-2" style={{ fontSize: 12 }}>
                          <i className="bi bi-check2 me-1"></i>{pdfFile.name}
                        </div>
                      )}
                    </>
                  )}
                </div>
                <input type="file" ref={fileInputRef} accept=".pdf,.doc,.docx,.txt"
                  style={{ display: 'none' }} onChange={handlePdfImport} />

                {/* Set metadata for imported note */}
                <div className="row g-2 mt-2">
                  <div className="col-6">
                    <label className="form-label" style={{ fontSize: 12 }}>Class</label>
                    <select className="form-select form-select-sm" value={form.class_grade}
                      onChange={e => sf('class_grade')(e.target.value)}>
                      <option value="">Select</option>
                      {classOptions.map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="form-label" style={{ fontSize: 12 }}>Subject</label>
                    <select className="form-select form-select-sm" value={form.subject}
                      onChange={e => sf('subject')(e.target.value)}>
                      <option value="">Select</option>
                      {subjectOpts.map(s => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="col-12">
                    <label className="form-label" style={{ fontSize: 12 }}>Chapter / Topic</label>
                    <input className="form-control form-control-sm"
                      value={form.chapter || ''} onChange={e => sf('chapter')(e.target.value)} />
                  </div>
                </div>

                {form.content && (
                  <button className="btn btn-success btn-sm mt-3 w-100"
                    onClick={() => setActiveTab('create')}>
                    <i className="bi bi-pencil me-1"></i>Review &amp; Save Imported Notes
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="col-lg-6">
            <div className="card h-100">
              <div className="card-header fw-semibold" style={{ fontSize: 14 }}>
                <i className="bi bi-eye me-2 text-primary"></i>Extracted Content Preview
              </div>
              <div className="card-body p-0">
                {form.content ? (
                  <textarea className="form-control border-0 rounded-0" rows={22}
                    value={form.content} onChange={e => sf('content')(e.target.value)}
                    style={{ resize: 'none', fontFamily: 'monospace', fontSize: 12 }} />
                ) : (
                  <div className="text-center text-muted py-5">
                    <i className="bi bi-file-earmark-pdf display-4 d-block mb-3 opacity-25"></i>
                    Upload a document to see extracted content here
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <AlertToast {...toast} onClose={() => setToast(t => ({ ...t, show: false }))} />
    </div>
  );
}
